import React, { useState } from 'react';

import useHandleClientClick from 'b2b/useHandleClientClick';

import { AutoAwesome } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import TabRound from '../../components/Tabs/TabRound';
import { clientsData } from '../Clients/clientsData';

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && (
        <Box sx={{ p: 0}}>
          {children}
        </Box>
      )}
    </div>
  );
};

const MeetingCard = ({ meeting }) => {
  const theme = useTheme();
  const client = clientsData.find(c => c.id === meeting.clientId);
  const [expanded, setExpanded] = useState(false);
  const [financialExpanded, setFinancialExpanded] = useState(false);
  const handleClientClick = useHandleClientClick();
  return (
    <Card 
      elevation={0}
      sx={{ 
        mb: 2,
        cursor: "pointer",
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.divider,
        '&:hover': {
          boxShadow: theme.shadows[1],
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box 
          title={"View client " + client?.name}
          onClick={() => handleClientClick(client?.name)}
          sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40,
              mr: 2
            }}
          >
            {client.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {client.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {meeting.title}
            </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={meeting.type}
                size="small"
                sx={{ 
                  fontWeight: 500
                }}
              />
              <Chip 
                label={meeting.status}
                size="small"
                sx={{ 
                  fontWeight: 500
                }}
              />
            </Box>
            {/* <IconButton 
              size="small" 
              sx={{ 
                ml: 1,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton> */}
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          mb: 2,
          color: 'text.secondary'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarMonthIcon sx={{ mr: 0.5, fontSize: 20 }} />
            <Typography variant="body2">
              {meeting.date}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            <Typography variant="body2">
              {meeting.time}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ mr: 0.5, fontSize: 20 }} />
            <Typography variant="body2">
              {meeting.location}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
            onClick={() => setFinancialExpanded(!financialExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Client Financial Overview
              </Typography>
            </Box>
            <IconButton size="small">
              <ExpandMoreIcon 
                sx={{ 
                  transform: financialExpanded ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s'
                }} 
              />
            </IconButton>
          </Box>

          <Collapse in={financialExpanded}>
            <Box sx={{ 
              p: 2, 
              bgcolor: theme.palette.background.paper, 
              borderRadius: 1,
              // mt: 1
            }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Portfolio Summary
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Total Assets</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>$2,450,000</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">YTD Return</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.success.main }}>+8.5%</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Risk Level</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>Moderate</Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Recent Changes
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Portfolio rebalanced on March 1st
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Added $50,000 to retirement account
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Updated risk tolerance questionnaire
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
              <div style={{paddingTop:"1rem"}}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleClientClick(client?.name)}
              >
                View Details
              </Button>
              </div>
            </Box>
          </Collapse>
        </Box>

        <Box>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AutoAwesome sx={{ mr: 1, color: theme.palette.warning.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                AI Meeting Prep Notes
              </Typography>
            </Box>
            <IconButton size="small">
              <ExpandMoreIcon 
                sx={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s'
                }} 
              />
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            <Box sx={{ 
              p: 2, 
              bgcolor: theme.palette.background.paper, 
              borderRadius: 1,
              mt: 1
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {meeting.prepNotes}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Key Discussion Points
              </Typography>
              <Stack spacing={1}>
                {meeting.keyPoints.map((point, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
};

const EventCard = ({ event }) => {
  const theme = useTheme();
  const client = clientsData.find(c => c.id === event.clientId);

  return (
    <Card 
      elevation={0}
      sx={{ 
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.divider,
        '&:hover': {
          boxShadow: theme.shadows[1],
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40,
                mr: 2
              }}
            >
              {client.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {client.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {event.title}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Button
              variant="contained"
              sx={{ 
                borderRadius: 10,
                fontWeight: 600,
                fontSize: '0.875rem',
                height: '24px'
              }}
            >
              {event.actionType}
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={event.priority}
                size="small"
                sx={{ 
                  bgcolor: event.priority === 'High' ? '#FFEBEE' : '#FFF3E0',
                  color: event.priority === 'High' ? '#D32F2F' : '#E65100',
                  fontWeight: 500,
                  height: '20px'
                }}
              />
                <Chip 
            label={event.type}
            size="small"
            sx={{ 
              bgcolor: theme.palette.grey[100],
              color: theme.palette.grey[700],
              fontWeight: 500,
              height: '20px'
            }}
          />
              {/* <Chip 
                label={event.status}
                size="small"
                sx={{ 
                  bgcolor: theme.palette.grey[100],
                  color: theme.palette.grey[700],
                  fontWeight: 500,
                  height: '20px'
                }}
              /> */}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          mb: 2,
          color: 'text.secondary'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarMonthIcon sx={{ mr: 0.5, fontSize: 20 }} />
            <Typography variant="body2">
              Action Period: {event.dateRange}
            </Typography>
          </Box>
        
        </Box>

        <h4
        style={{color:"#000"}} 
        >
          {event.description}
        </h4>
      </CardContent>
    </Card>
  );
};

const MeetingEvents = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const theme = useTheme();

  // Helper function to format date ranges
  const getDateRange = (startDaysFromToday, endDaysFromToday) => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + startDaysFromToday);
    
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + endDaysFromToday);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Sample meetings data
  const meetings = [
    {
      id: 1,
      clientId: 1,
      title: "Annual Portfolio Review",
      date: getDateRange(3, 5),
      time: "10:00 AM - 11:30 AM",
      location: "Virtual Meeting",
      type: "Portfolio Review",
      status: "Confirmed",
      prepNotes: "Client has shown interest in sustainable investments. Recent market volatility has impacted their portfolio by -2.3%. Consider discussing risk tolerance and potential rebalancing opportunities.",
      keyPoints: [
        "Review performance of ESG investments",
        "Discuss impact of recent market downturn",
        "Evaluate current asset allocation",
        "Explore tax-loss harvesting opportunities",
        "Update risk tolerance assessment"
      ]
    },
    {
      id: 2,
      clientId: 2,
      title: "Retirement Planning Strategy",
      date: getDateRange(5, 7),
      time: "2:00 PM - 3:00 PM",
      location: "Office Meeting",
      type: "Retirement",
      status: "Scheduled",
      prepNotes: "Client is approaching retirement age and has expressed concerns about market conditions. Their portfolio is heavily weighted in tech stocks. Consider discussing diversification strategies.",
      keyPoints: [
        "Review retirement income projections",
        "Discuss Social Security claiming strategy",
        "Evaluate current withdrawal rate",
        "Analyze tax-efficient distribution plan",
        "Review healthcare coverage options"
      ]
    },
    {
      id: 3,
      clientId: 3,
      title: "Estate Planning Update",
      date: getDateRange(7, 9),
      time: "11:00 AM - 12:00 PM",
      location: "Virtual Meeting",
      type: "Estate",
      status: "Pending",
      prepNotes: "Client has recently welcomed a grandchild and needs to update estate planning documents. Consider discussing trust options and beneficiary designations.",
      keyPoints: [
        "Review current estate plan",
        "Discuss trust options for grandchild",
        "Update beneficiary designations",
        "Review tax implications",
        "Discuss charitable giving strategy"
      ]
    }
  ];

  // Sample events data
  const events = [
    {
      id: 1,
      clientId: 1,
      title: "Portfolio Rebalancing Required",
      description: "Asset allocation has deviated by 7% from target. Current allocation: 65% equities, 35% fixed income. Target: 60% equities, 40% fixed income.",
      dateRange: getDateRange(0, 7),
      type: "Portfolio",
      priority: "High",
      status: "Pending",
      actionType: "Plan & Scenario"
    },
    {
      id: 2,
      clientId: 2,
      title: "RMD Due for IRA Account",
      description: "Required Minimum Distribution of $45,000 due for client's Traditional IRA. Last year's RMD was $42,000.",
      dateRange: getDateRange(3, 10),
      type: "Compliance",
      priority: "High",
      status: "Scheduled",
      actionType: "Notify Client"
    },
    {
      id: 3,
      clientId: 3,
      title: "Annual Plan Review Due",
      description: "Comprehensive financial plan review required. Last review completed 11 months ago.",
      dateRange: getDateRange(5, 12),
      type: "Planning",
      priority: "Medium",
      status: "Upcoming",
      actionType: "Schedule Meeting"
    },
    {
      id: 4,
      clientId: 1,
      title: "Tax Planning Opportunity",
      description: "Client has significant capital gains this year. Consider tax-loss harvesting strategies.",
      dateRange: getDateRange(-2, 5),
      type: "Tax",
      priority: "Medium",
      status: "New",
      actionType: "Plan & Scenario"
    },
    {
      id: 5,
      clientId: 2,
      title: "Insurance Policy Renewal",
      description: "Life insurance policy renewal due. Premium increased by 5% based on age bracket change.",
      dateRange: getDateRange(2, 9),
      type: "Insurance",
      priority: "Medium",
      status: "In Progress",
      actionType: "Notify Client"
    }
  ];

  const tabList = ["Meetings", "AI Recommended Actions"];

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      bgcolor: theme.palette.background.default,
      boxSizing: 'border-box',
      padding:"0.5rem"
    }}>
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'transparent'
        }}
      >
        <div style={{paddingBottom:"1rem"}}>
        <TabRound 
          options={tabList}
          value={tabIndex}
          onChange={setTabIndex}
          height="48px"
          fontSize="1rem"
          variant="line"
        />
        </div>

        <TabPanel value={tabIndex} index={0}>
          {/* <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
            Upcoming Meetings
          </Typography> */}
          {meetings.map(meeting => (
            <MeetingCard key={meeting.id} meeting={meeting} />
          ))}
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          {/* <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
            Client Events
          </Typography> */}
          {events.map(event => (
            <EventCard key={event.id} event={event} />
          ))}
        </TabPanel>

      </Paper>
    </Box>
  );
};

export default MeetingEvents;
